import { cache } from 'react'
import { getEntry, listEntries } from '@/lib/cms'
import type { Block, Entry, MediaObject } from '@/lib/types'

/**
 * The site's own content, read off the API's generic entry shape.
 *
 * `Entry.data` is `Record<string, unknown>` by contract — the API serves
 * whatever the content type stored, and refuses to promise more. These readers
 * are where that becomes typed: one place that knows a project has `chapters`
 * and a technology has `pitfalls`, so a page component never reaches into
 * `unknown` and never crashes on a field an editor left empty.
 *
 * Every reader is total. A missing string reads as `''` and a missing list as
 * `[]`, because half a page rendered is the right answer to a half-filled
 * entry, and a thrown error is not.
 */

type Data = Record<string, unknown>

function data(entry: Entry): Data {
    return Array.isArray(entry.data) ? {} : entry.data
}

function text(source: Data, key: string): string {
    const value = source[key]

    return typeof value === 'string' ? value : ''
}

function strings(source: Data, key: string): string[] {
    const value = source[key]

    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

function records(source: Data, key: string): Data[] {
    const value = source[key]

    return Array.isArray(value)
        ? value.filter(
              (item): item is Data => typeof item === 'object' && item !== null && !Array.isArray(item),
          )
        : []
}

/**
 * The `media` object the API leaves where the panel stored a `media_item_id`.
 * Null both when nothing was selected and when the selected item has no file.
 */
function media(source: Data, key = 'media'): MediaObject | null {
    const value = source[key]

    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        return null
    }

    const candidate = value as MediaObject

    return typeof candidate.url === 'string' ? candidate : null
}

export type Figure = { value: string; text: string }
export type Titled = { title: string; text: string }

export type Service = {
    slug: string
    url: string
    indexing: Indexing
    title: string
    shortTitle: string
    summary: string
    duration: string
    startingAt: string
    deliverables: string[]
    /** The four numbered cells the design sets under a service's introduction. */
    highlights: Titled[]
    outcomes: Figure[]
    technologies: string[]
    projects: string[]
    /** The articles written under this service, as slugs. */
    articles: string[]
    /** The long-form body, written in the block builder. */
    blocks: Block[]
}

export type ProjectImage = { place: string; text: string; media: MediaObject | null }

export type Project = {
    slug: string
    url: string
    indexing: Indexing
    title: string
    kind: string
    year: string
    stackShort: string
    summary: string
    intro: string
    media: MediaObject | null
    facts: { label: string; value: string }[]
    figures: Figure[]
    images: ProjectImage[]
    technologies: string[]
    services: string[]
    tags: string[]
    blocks: Block[]
}

export type Technology = {
    slug: string
    url: string
    indexing: Indexing
    title: string
    purpose: string
    purposeSlug: string
    summary: string
    since: string
    frequency: string
    usage: string[]
    projects: string[]
    blocks: Block[]
}

export type Question = { question: string; answer: string }

export type Article = {
    slug: string
    url: string
    indexing: Indexing
    title: string
    category: string
    categorySlug: string
    /**
     * The service this article was written under, as a slug.
     *
     * A category is how a reader groups these — *Praktijk*, *Techniek*. The
     * pillar is how the site does: every article belongs beneath one of the
     * five services, and both pages link to the other.
     */
    pillar: string
    publishedAt: string | null
    date: string
    updated: string
    readingTime: string
    words: string
    intro: string
    keyPoints: string[]
    closing: string
    faq: Question[]
    related: string[]
    blocks: Block[]
}

export type PageCopy = {
    slug: string
    title: string
    heading: string
    intro: string
    media: MediaObject | null
    faq: Question[]
    steps: Titled[]
    stats: Figure[]
    marquee: string[]
    blocks: Block[]
    /** When the CMS last published this, for a page that has to say so. */
    publishedAt: string | null
}

function titled(source: Data[]): Titled[] {
    return source.map((item) => ({ title: text(item, 'title'), text: text(item, 'text') }))
}

function figures(source: Data[]): Figure[] {
    return source.map((item) => ({ value: text(item, 'value'), text: text(item, 'text') }))
}

function questions(source: Data[]): Question[] {
    return source.map((item) => ({ question: text(item, 'question'), answer: text(item, 'answer') }))
}

function termIn(entry: Entry, taxonomy: string): { name: string; slug: string } {
    const term = entry.terms.find((candidate) => candidate.taxonomy === taxonomy)

    return { name: term?.name ?? '', slug: term?.slug ?? '' }
}

/** A Dutch long-form date, which is how every date on this site is written. */
export function dutchDate(iso: string | null): string {
    if (iso === null) {
        return ''
    }

    return new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }).format(
        new Date(iso),
    )
}

export type Heading = { anchor: string; title: string }

/**
 * The sections of a body, for a table of contents.
 *
 * Only the h2s: those are the sections a reader navigates between, and the
 * h3s under them are the steps inside one. A heading with no anchor cannot be
 * linked to, so it is not offered as a link.
 */
export function headingsIn(blocks: Block[]): Heading[] {
    return blocks
        .filter((block) => block.type === 'heading' && (block.data.level ?? 'h2') === 'h2')
        .map((block) => ({
            anchor: typeof block.data.anchor === 'string' ? block.data.anchor : '',
            title: typeof block.data.text === 'string' ? block.data.text : '',
        }))
        .filter((heading) => heading.anchor !== '' && heading.title !== '')
}

/**
 * What the panel's SEO tab says about this entry, in the shape Next's metadata
 * wants it.
 *
 * `noindex` is the one an editor reaches for on a page that exists but should
 * not be found — a landing page for one campaign, a case published early. It
 * carries `nofollow` as a separate switch because they are separate questions:
 * a page can be worth not indexing while its links are still worth following.
 */
export type Indexing = { index: boolean; follow: boolean; canonical: string | null }

export function indexing(entry: Entry): Indexing {
    const seo = entry.seo as Record<string, unknown>
    const canonical = typeof seo.canonical === 'string' && seo.canonical !== '' ? seo.canonical : null

    return { index: seo.noindex !== true, follow: seo.nofollow !== true, canonical }
}

/** `01`, `02`, … — the counter the design puts in front of almost every row. */
export function ordinal(index: number): string {
    return String(index + 1).padStart(2, '0')
}

export function toService(entry: Entry): Service {
    const source = data(entry)

    return {
        slug: entry.slug,
        url: entry.url,
        indexing: indexing(entry),
        title: entry.title,
        shortTitle: text(source, 'short_title') || entry.title.toLowerCase(),
        summary: text(source, 'summary'),
        duration: text(source, 'duration'),
        startingAt: text(source, 'starting_at'),
        deliverables: strings(source, 'deliverables'),
        highlights: titled(records(source, 'highlights')),
        outcomes: figures(records(source, 'outcomes')),
        technologies: strings(source, 'technologies'),
        projects: strings(source, 'projects'),
        articles: strings(source, 'articles'),
        blocks: entry.blocks,
    }
}

export function toProject(entry: Entry): Project {
    const source = data(entry)

    return {
        slug: entry.slug,
        url: entry.url,
        indexing: indexing(entry),
        title: entry.title,
        kind: text(source, 'kind'),
        year: text(source, 'year'),
        stackShort: text(source, 'stack_short'),
        summary: text(source, 'summary'),
        intro: text(source, 'intro'),
        media: media(source),
        facts: records(source, 'facts').map((fact) => ({
            label: text(fact, 'label'),
            value: text(fact, 'value'),
        })),
        figures: figures(records(source, 'figures')),
        images: records(source, 'images').map((image) => ({
            place: text(image, 'place'),
            text: text(image, 'text'),
            media: media(image),
        })),
        technologies: strings(source, 'technologies'),
        services: strings(source, 'services'),
        tags: entry.terms.filter((term) => term.taxonomy === 'tag').map((term) => term.name),
        blocks: entry.blocks,
    }
}

export function toTechnology(entry: Entry): Technology {
    const source = data(entry)
    const purpose = termIn(entry, 'purpose')

    return {
        slug: entry.slug,
        url: entry.url,
        indexing: indexing(entry),
        title: entry.title,
        purpose: purpose.name,
        purposeSlug: purpose.slug,
        summary: text(source, 'summary'),
        since: text(source, 'since'),
        frequency: text(source, 'frequency'),
        usage: strings(source, 'usage'),
        projects: strings(source, 'projects'),
        blocks: entry.blocks,
    }
}

export function toArticle(entry: Entry): Article {
    const source = data(entry)
    const category = termIn(entry, 'category')

    return {
        slug: entry.slug,
        url: entry.url,
        indexing: indexing(entry),
        title: entry.title,
        category: category.name,
        categorySlug: category.slug,
        pillar: text(source, 'pillar'),
        publishedAt: entry.published_at,
        date: dutchDate(entry.published_at),
        updated: text(source, 'updated'),
        readingTime: text(source, 'reading_time'),
        words: text(source, 'words'),
        intro: text(source, 'intro'),
        keyPoints: strings(source, 'key_points'),
        closing: text(source, 'closing'),
        faq: questions(records(source, 'faq')),
        related: strings(source, 'related'),
        blocks: entry.blocks,
    }
}

export function toPageCopy(entry: Entry): PageCopy {
    const source = data(entry)

    return {
        slug: entry.slug,
        title: entry.title,
        heading: text(source, 'heading') || entry.title,
        intro: text(source, 'intro'),
        media: media(source),
        faq: questions(records(source, 'faq')),
        steps: titled(records(source, 'steps')),
        stats: figures(records(source, 'stats')),
        marquee: strings(source, 'marquee'),
        blocks: entry.blocks,
        publishedAt: entry.published_at,
    }
}

/* Fetchers ---------------------------------------------------------------- */

/**
 * A whole content type, in panel order.
 *
 * These listings are small — five services, six projects, forty technologies
 * — and every page needs all of one of them, so they are read in one page of
 * 100 rather than followed cursor by cursor. A type that outgrows that gets
 * pagination here, not at every call site.
 *
 * The default order is the one set in the panel: these types are sets, not
 * streams, and their numbering on the page is their order in the CMS.
 *
 * Wrapped in React's `cache` so one render pays for one listing once: a detail
 * page reads services for its cross-links and again for its "other services"
 * block, and the API is rate-limited per address.
 */
const all = cache(async (type: string, sort: string = 'order'): Promise<Entry[]> => {
    const page = await listEntries({ type, perPage: 100, sort })

    return page.data
})

export async function getServices(): Promise<Service[]> {
    return (await all('service')).map(toService)
}

export async function getProjects(): Promise<Project[]> {
    return (await all('project')).map(toProject)
}

/**
 * The same projects, most recent first.
 *
 * The listing itself stays in panel order — a service page numbers its cases
 * the way an editor arranged them, and the home page's slider leads with the
 * one that should be seen first. The index at `/werk` is the one place that
 * reads as a history, and a history that opens on 2023 says the work stopped
 * there.
 *
 * Sorting is by year alone, so the panel keeps the say over projects that
 * share one: `sort` is stable, and equal years come out in the order they
 * arrived in. A project with no year is oldest rather than first — an empty
 * field should not jump the queue.
 */
export function newestFirst(projects: Project[]): Project[] {
    return [...projects].sort((a, b) => year(b) - year(a))
}

function year(project: Project): number {
    const parsed = Number.parseInt(project.year, 10)

    return Number.isNaN(parsed) ? 0 : parsed
}

export async function getTechnologies(): Promise<Technology[]> {
    return (await all('technology')).map(toTechnology)
}

/** The blog is a stream, so it reads newest first rather than in panel order. */
export async function getArticles(): Promise<Article[]> {
    return (await all('post', '-published_at')).map(toArticle)
}

export async function getService(slug: string, token?: string): Promise<Service | null> {
    const entry = await getEntry('service', slug, { token })

    return entry === null ? null : toService(entry)
}

export async function getProject(slug: string, token?: string): Promise<Project | null> {
    const entry = await getEntry('project', slug, { token })

    return entry === null ? null : toProject(entry)
}

export async function getTechnology(slug: string, token?: string): Promise<Technology | null> {
    const entry = await getEntry('technology', slug, { token })

    return entry === null ? null : toTechnology(entry)
}

export async function getArticle(slug: string, token?: string): Promise<Article | null> {
    const entry = await getEntry('post', slug, { token })

    return entry === null ? null : toArticle(entry)
}

/**
 * The copy that belongs to a fixed page — its heading, its intro, the FAQ
 * under it. Null when the page has not been created in the panel, which every
 * page component treats as "render the layout without that block" rather than
 * as a 404: the route exists because the code says so, not because an entry
 * does.
 */
export async function getPageCopy(slug: string, token?: string): Promise<PageCopy | null> {
    const entry = await getEntry('page', slug, { token })

    return entry === null ? null : toPageCopy(entry)
}

/**
 * A service written for one town: `/diensten/{dienst}/{plaats}`.
 *
 * Stored as an ordinary page under the slug `{service}-{town}`, so the panel
 * edits it like any other page and the API needs to know nothing about the
 * arrangement. What makes it local is in its data, and that is what this reads.
 */
export type LocalPage = {
    slug: string
    url: string
    indexing: Indexing
    title: string
    service: string
    town: string
    townName: string
    intro: string
    description: string
    facts: { label: string; value: string }[]
    faq: Question[]
    blocks: Block[]
}

function toLocalPage(entry: Entry): LocalPage {
    const source = data(entry)
    const service = text(source, 'service')
    const town = text(source, 'town')

    return {
        slug: entry.slug,
        url: `/diensten/${service}/${town}`,
        indexing: indexing(entry),
        title: entry.title,
        service,
        town,
        townName: text(source, 'town_name'),
        intro: text(source, 'intro'),
        description: typeof entry.seo?.description === 'string' ? entry.seo.description : '',
        facts: records(source, 'facts').map((fact) => ({
            label: text(fact, 'label'),
            value: text(fact, 'value'),
        })),
        faq: questions(records(source, 'faq')),
        blocks: entry.blocks,
    }
}

/** Every local page there is, which is what the sitemap and the routes need. */
export const getLocalPages = cache(async (): Promise<LocalPage[]> => {
    const pages = await all('page')

    return pages.map(toLocalPage).filter((page) => page.service !== '' && page.town !== '')
})

export async function getLocalPage(service: string, town: string, token?: string): Promise<LocalPage | null> {
    const entry = await getEntry('page', `${service}-${town}`, { token })

    return entry === null ? null : toLocalPage(entry)
}

/** Keeps the order of `slugs`, which is the order an editor chose. */
export function bySlugs<T extends { slug: string }>(items: T[], slugs: string[]): T[] {
    const index = new Map(items.map((item) => [item.slug, item]))

    return slugs.map((slug) => index.get(slug)).filter((item): item is T => item !== undefined)
}
